<template>
  <div
      class="bg-white rounded-lg p-2 mb-2 shadow-sm flex flex-col gap-2 justify-between hover:shadow-md transition-shadow cursor-move"
  >
    <div class="flex gap-2">
      <div class="flex-1 w-full">
        <div class="flex items-center gap-2">
          <div class="text-lg py-1 font-bold">
            {{ job.customer?.name || 'Unknown' }}
          </div>
          <!-- <div v-if="settings.getValue('customers_names_to_be_viewed_glasses_screen') == 'full_name'" class="text-lg py-1 font-bold">
            {{ job.customer?.name || 'Unknown' }}
          </div> -->
          <!-- <div v-if="settings.getValue('customers_names_to_be_viewed_glasses_screen') == 'only_first_two_names'" class="text-lg py-1 font-bold">
            {{ String(job.customer?.name).split(" ").splice(0, 2).join(" ") || 'Unknown' }}
          </div> -->
        </div>
        <p v-if="job.customer?.phone" class="text-sm text-gray-600">
          {{ job.customer.phone }}
        </p>

        <!-- Inline Notes Editing -->
        <div v-if="isEditing">
          <textarea
              :value="editingNotes"
              @input="$emit('update:editingNotes', ($event.target as HTMLTextAreaElement).value)"
              class="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              @blur="$emit('saveNotes')"
              @keyup.enter="$emit('saveNotes')"
              ref="notesTextarea"
              rows="3"
          ></textarea>
          <button type="button"
              @click="$emit('saveNotes')"
              class="px-3 py-1 bg-blue-500 text-white text-sm rounded-md hover:bg-blue-600"
          >
            {{ t('save') }}
          </button>
        </div>
        <p
            v-else
            @click="$emit('editNotes')"
            class="text-gray-600 cursor-pointer hover:bg-gray-100 p-2 rounded-md"
        >
          {{ job.notes || t('clickToAddNotes') }}
        </p>
      </div>

      <div>
        <p class="text-end me-1 text-lg font-bold text-blue-500">
          <TimeCounter :start-date="job.createdAt" />
        </p>
      </div>
    </div>

    <!-- Action Buttons -->
    <div class="relative flex items-center justify-between gap-2 ml-4">
      <div class="flex gap-2 justify-center grow">
        <!-- Left arrow button -->
        <button type="button"
            v-if="job.status !== 'waiting'"
            @click="handleLeftClick"
            class="px-3 py-1 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
            :title="getLeftButtonTitle()"
        >
          <Icon v-if="$ctx.locale == 'en'" icon="mingcute:left-fill" width="24" height="24" />
          <Icon v-else icon="mingcute:right-fill" width="24" height="24" />
        </button>

        <!-- Right arrow button -->
        <button type="button"
            v-if="job.status !== 'ready'"
            @click="handleRightClick"
            class="px-3 py-1 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
            :title="getRightButtonTitle()"
        >
          <Icon v-if="$ctx.locale == 'en'" icon="mingcute:right-fill" width="24" height="24" />
          <Icon v-else icon="mingcute:left-fill" width="24" height="24" />
        </button>

        <!-- Done button for all statuses -->
        <button
            @click="$emit('delete')"
            class="px-3 py-1 bg-green-100 text-green-800 rounded-md hover:bg-green-200 transition-colors"
            :title="t('done')"
        >
          <Icon icon="ic:baseline-check" width="24" height="24" />
        </button>
      </div>
      <p class="mt-1 mx-2 text-xs text-gray-400">
        {{ t('added') }}: {{ formatTime(job.createdAt) }}
      </p>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n'
import { ref, watch, nextTick } from 'vue'
import GlassesJob from '@/models/customer/GlassesJob'
import TimeCounter from '@/views/customers/TimeCounter.vue'
import { Icon } from '@iconify/vue'
import { useSettingsStore } from '@/stores/settingsStore'

interface Props {
  job: GlassesJob
  isEditing?: boolean
  editingNotes?: string
}

const props = defineProps<Props>()
const emit = defineEmits(['moveToWaiting', 'moveToProgress', 'moveToReady', 'editNotes', 'delete', 'saveNotes', 'update:editingNotes'])
const settings = useSettingsStore();

const notesTextarea = ref<HTMLTextAreaElement | null>(null)

// Auto-focus textarea when editing starts
watch(() => props.isEditing, async (newVal) => {
  if (newVal) {
    await nextTick()
    notesTextarea.value?.focus()
  }
})

const { t } = useI18n({
  messages: {
    en: {
      moveToWaiting: 'Move to Waiting',
      moveToInProgress: 'Move to In Progress',
      moveToReady: 'Move to Ready',
      editNotes: 'Edit Notes',
      delete: 'Delete',
      done: 'Done',
      clickToAddNotes: 'Click to add pouch number',
      save: 'Save',
      added: 'Added'
    },
    ar: {
      moveToWaiting: 'نقل إلى الانتظار',
      moveToInProgress: 'نقل إلى قيد الإنجاز',
      moveToReady: 'نقل إلى جاهزة',
      editNotes: 'تعديل الملاحظات',
      delete: 'حذف',
      done: 'تم',
      clickToAddNotes: 'انقر لإضافة رقم المحفظة',
      save: 'حفظ',
      added: 'تمت الإضافة'
    }
  }
})

function formatTime(date: Date): string {
  if (!date) return ''
  try {
    const d = new Date(date)
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  } catch (error) {
    console.error('Error formatting date:', error)
    return ''
  }
}

function handleLeftClick() {
  if (props.job.status === 'in_progress') {
    emit('moveToWaiting')
  } else if (props.job.status === 'ready') {
    emit('moveToProgress')
  }
}

function handleRightClick() {
  if (props.job.status === 'waiting') {
    emit('moveToProgress')
  } else if (props.job.status === 'in_progress') {
    emit('moveToReady')
  }
}

function getLeftButtonTitle(): string {
  if (props.job.status === 'in_progress') {
    return t('moveToWaiting')
  } else if (props.job.status === 'ready') {
    return t('moveToInProgress')
  }
  return ''
}

function getRightButtonTitle(): string {
  if (props.job.status === 'waiting') {
    return t('moveToInProgress')
  } else if (props.job.status === 'in_progress') {
    return t('moveToReady')
  }
  return ''
}
</script>

<style scoped>
.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
